<template>
  <div>
    <v-row>
      <v-col>
        <v-col
          v-if="!isPaymentElementReady && !initializationError"
          cols="12"
          class="payment-element-loader"
          data-test="qr-checkout-payment-element-loader"
        >
          <v-progress-circular indeterminate :width="3" color="candy" />
        </v-col>

        <v-alert v-if="initializationError" type="error" outlined data-test="qr-checkout-payment-element-init-error">
          {{ initializationError }}
        </v-alert>

        <!-- Payment Element Wrapper -->
        <div v-show="isPaymentElementReady">
          <div
            id="payment-element"
            ref="paymentElementRef"
            class="payment-element-container"
            data-test="qr-checkout-payment-element-container"
          >
            <!-- Stripe Payment Element will be mounted here -->
          </div>

          <!-- Bank Transfer Link -->
          <div v-if="isEurCurrency() && !isUserOnHideInvoiceAbTest" class="mt-6 d-flex align-center">
            <v-tooltip
              bottom
              max-width="800"
              nudge-right="200"
              transition="none"
              :disabled="$vuetify.breakpoint.smAndDown"
              data-test="qr-checkout-bank-transfer-tooltip"
            >
              <template #activator="{ on }">
                <v-btn
                  class="v-btn--text--link"
                  text
                  :ripple="false"
                  color="primary"
                  data-test="qr-checkout-select-bank-transfer-button"
                  @click="handleBankTransferClick"
                  v-on="on"
                >
                  <span class="navy--text">{{ $t('fields.checkout.form.prefer_bank_transfer') }}</span>
                </v-btn>
              </template>
              <span>{{ $t('fields.checkout.form.bank_transfer_tooltip') }}</span>
            </v-tooltip>

            <div class="d-flex d-md-none ml-2">
              <v-tooltip bottom max-width="800" transition="none" data-test="qr-checkout-bank-transfer-tooltip-mobile">
                <template #activator="{ on }">
                  <Icon
                    name="help-round-system"
                    outline
                    :size="24"
                    color="navy"
                    class="bank-transfer-help-icon"
                    data-test="qr-checkout-bank-transfer-help-icon"
                    v-on="on"
                  />
                </template>
                <span>{{ $t('fields.checkout.form.bank_transfer_tooltip') }}</span>
              </v-tooltip>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Stripe, StripeElements, StripePaymentElement, TermsOption } from '@stripe/stripe-js';
import AppConfig from '@/AppConfig';
import { Plan } from '@/classes/stripe';
import axios from 'axios';
import * as Sentry from '@sentry/vue';
import StripeService from '@/Services/Subscription/StripeService';
import Icon from '@/components/Icons/Icon.vue';
import { Team } from '@/classes/auth';
import Authenticator from '@/Services/Auth/Authenticator';
import { Currencies } from '@/Models/Localization';
import OptimizelyService from '@/Services/OptimizelyService';
import store from '@/store';

@Component({
  components: { Icon },
})
export default class PaymentElement extends Vue {
  @Prop({ default: () => new Plan() })
  selectedPlan!: Plan;
  elements: StripeElements | null = null;
  paymentElement: StripePaymentElement | null = null;
  isPaymentElementReady = false;
  selectedPaymentMethodType = '';
  clientSecret!: string;
  setupIntentId!: string;
  initializationError: string = '';
  isUserOnHideInvoiceAbTest: boolean = OptimizelyService.isUserInAbTest(
    store.state?.auth?.user,
    OptimizelyService.HIDE_INVOICE_PAYMENT_TEST_ID
  );

  $refs!: {
    paymentElementRef: HTMLElement;
  };

  get team(): Team {
    return Authenticator.getTeam();
  }

  get stripeService(): StripeService {
    return this.$store.state.subscription.stripeService;
  }

  get stripe(): Stripe | null {
    return this.stripeService?.stripe || null;
  }

  async mounted(): Promise<void> {
    try {
      await this.initializePaymentElements();
    } catch (error) {
      Sentry.captureException(error);
      this.initializationError = this.$t('fields.checkout.payment_element.initialization_failed').toString();
    }
  }

  async reinitialize(): Promise<void> {
    try {
      // Clean up existing elements
      if (this.paymentElement) {
        this.paymentElement.unmount();
        this.paymentElement = null;
      }

      // Reset state
      this.isPaymentElementReady = false;
      this.initializationError = '';
      this.elements = null;

      // Reinitialize
      await this.initializePaymentElements();
    } catch (error) {
      Sentry.captureException(error);
      this.initializationError = this.$t('fields.checkout.payment_element.initialization_failed').toString();
    }
  }

  async initializePaymentElements(): Promise<void> {
    if (!this.stripe) {
      throw new Error('Stripe not initialized');
    }

    // Create SetupIntent for subscription flow
    await this.createSetupIntent();
    if (!this.clientSecret) {
      throw new Error('SetupIntent creation failed - no client secret received');
    }

    // Create Elements instance with SetupIntent and locale
    this.elements = this.stripe.elements({
      clientSecret: this.clientSecret,
      locale: this.$store.state?.auth?.user?.language ?? 'en',
    });

    // Create Payment Element
    this.paymentElement = this.elements.create('payment', {
      layout: 'accordion',
      terms: {
        card: 'never',
        paypal: 'never',
        googlePay: 'never',
        applePay: 'never',
        klarna: 'never',
      } as TermsOption,
      fields: {
        // We gather billing information separately through our own form on the payments page
        billingDetails: { address: 'never' },
      },
    });

    // Mount Payment Element
    this.paymentElement.mount('#payment-element');

    // Listen for payment element events
    this.paymentElement.on('ready', () => {
      this.isPaymentElementReady = true;
      this.initializationError = ''; // Clear any previous errors
      this.$emit('payment-element-ready');
    });

    this.paymentElement.on('change', (event: any) => {
      // Track the selected payment method type
      if (event.value?.type) {
        this.selectedPaymentMethodType = event.value.type;
      }
    });
  }

  async createSetupIntent(): Promise<void> {
    // Create SetupIntent for subscription flow
    const response = await axios.post(AppConfig.getAPIBaseUrl() + '/subscription/stripe/payment-methods/create', {
      use_dynamic_payment_methods: true,
    });
    this.clientSecret = response.data.client_secret;
    this.setupIntentId = response.data.id;
  }

  get isReady(): boolean {
    return this.isPaymentElementReady;
  }

  get paymentMethodType(): string {
    return this.selectedPaymentMethodType;
  }

  handleBankTransferClick(): void {
    this.$emit('switch-to-bank-transfer');
  }

  setIsReady(isReady: boolean): void {
    this.isPaymentElementReady = isReady;
  }

  isEurCurrency(): boolean {
    return this.team.currency.toLowerCase() === Currencies.EUR;
  }

  beforeDestroy(): void {
    // Clean up PaymentElement when component is destroyed
    if (this.paymentElement) {
      this.paymentElement.unmount();
      this.paymentElement = null;
    }
  }
}
</script>

<style scoped lang="scss">
.payment-element-loader {
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 240px;
}

.payment-element-container {
  z-index: 2;
}

.bank-transfer-help-icon {
  &:hover {
    cursor: pointer;
  }
}
</style>
